import {useCallback, useEffect, useMemo, useState} from 'react';
import axios, {CancelToken} from 'axios';
import useSavedComponentsState from '../../../../../../../hooks/use-saved-components-state';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';
import {SearchDataSettingsOrderBy} from '../../../../../../../hooks/use-search';
import {DashboardWidgetConfig} from '../../dashboard-widget.config';
import {useNumberVisibilityMode} from '../use-number-visibility-mode';
import {
    AugmentedDashboardWidgetEntry,
    DashboardWidgetEntry,
    DashboardWidgetLayoutConfig,
    DashboardWidgetType,
    SavedUiComponentStateWidgetConfig,
} from '../../types';

export const STATIC_STATS_SAVED_STATE_KEYS = {
    customer: {name: 'customer-information-stats', version: '1.0.0'},
    product: {name: 'product-information-stats', version: '1.0.0'},
    vendor: {name: 'vendor-information-stats', version: '1.0.0'},
} as const;

export interface StaticStatsSavedWidget {
    connectionId: string;
    orderBy?: SearchDataSettingsOrderBy;
    type?: DashboardWidgetType;
    variant?: string;
    underlyingDataVisibility?: Record<string, boolean>;
}

export interface StaticStatsSavedState {
    widgets: StaticStatsSavedWidget[];
    selectedCategory?: string;
}

export interface CategorizedStaticDashboardWidgets {
    category: string;
    widgets: AugmentedDashboardWidgetEntry[];
}

export interface UseStaticStatsWidgetsStateOptions {
    name: string;
    version: string;
}

/**
 * Upserts a widget entry into the saved widgets list.
 */
const upsertSavedWidget = (
    prev: StaticStatsSavedWidget[],
    connectionId: string,
    patch: Partial<Omit<StaticStatsSavedWidget, 'connectionId'>>,
): StaticStatsSavedWidget[] => {
    const existing = prev.find(e => e.connectionId === connectionId);
    if (existing) {
        return prev.map(e => e.connectionId === connectionId ? {...e, ...patch} : e);
    }
    return [...prev, {connectionId, ...patch}];
};

/**
 * Persists and restores widget settings (variant/type/visibility), orderBy, and optional
 * category tab for static stats pages (customer / product information views).
 */
const useStaticStatsWidgetsState = ({name, version}: UseStaticStatsWidgetsStateOptions) => {
    const isMounted = useIsMounted();
    const {mode: numberVisibilityMode} = useNumberVisibilityMode();
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [savingWidgetConfig, setSavingWidgetConfig] = useState(false);
    const [savedWidgets, setSavedWidgets] = useState<StaticStatsSavedWidget[]>([]);
    const [selectedCategory, setSelectedCategoryState] = useState<string | undefined>();

    const {highestVersion: getCachedState, save: saveCachedState} = useSavedComponentsState<StaticStatsSavedState>({
        name,
        version,
    });

    /**
     * Loads the highest-version saved state for this stats page.
     */
    const loadCachedState = useCallback(async (cancelToken?: CancelToken) => {
        setLoadingConfig(true);
        const cached = await getCachedState(cancelToken);
        if (!isMounted() || cancelToken?.reason) {
            return;
        }
        setSavedWidgets(cached?.data?.widgets ?? []);
        if (cached?.data?.selectedCategory) {
            setSelectedCategoryState(cached.data.selectedCategory);
        }
        setLoadingConfig(false);
    }, [getCachedState, isMounted]);

    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void loadCachedState(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [loadCachedState]);

    /**
     * Merges a single static widget with saved prefs and rebuilds its configuration.
     */
    const augmentWidget = useCallback((widget: AugmentedDashboardWidgetEntry): AugmentedDashboardWidgetEntry => {
        const connectionId = widget.connectionId || widget.id;
        const saved = savedWidgets.find(e => e.connectionId === connectionId);
        const type = saved?.type ?? widget.type;
        const variant = saved?.variant ?? widget.variant;
        const config = new DashboardWidgetConfig(widget.id, variant, type, numberVisibilityMode);
        return {
            ...widget,
            connectionId,
            type: type ?? config.type!,
            variant: config.variant,
            configuration: config.getDataConfig(),
            underlyingDataVisibility: saved?.underlyingDataVisibility ?? widget.underlyingDataVisibility,
            orderBy: saved?.orderBy ?? widget.orderBy,
            size: widget.size ?? config.sizeInformation?.size,
        };
    }, [numberVisibilityMode, savedWidgets]);

    /**
     * Augments categorized static widget groups with persisted settings.
     */
    const augmentCategorizedWidgets = useCallback((groups: CategorizedStaticDashboardWidgets[] | undefined): CategorizedStaticDashboardWidgets[] => {
        if (!groups?.length) {
            return [];
        }
        return groups.map(group => ({
            ...group,
            widgets: group.widgets.map(augmentWidget),
        }));
    }, [augmentWidget]);

    /**
     * Saves orderBy for a widget into the page-scoped saved component state.
     */
    const saveWidgetOrderBy = useCallback(async (data: SavedUiComponentStateWidgetConfig) => {
        await saveCachedState(prev => {
            const nextWidgets = upsertSavedWidget(prev?.widgets ?? [], data.connectionId, {orderBy: data.orderBy});
            setSavedWidgets(nextWidgets);
            return {
                widgets: nextWidgets,
                selectedCategory: prev?.selectedCategory,
            };
        });
    }, [saveCachedState]);

    /**
     * Saves variant/type/visibility for a widget into the page-scoped saved component state.
     */
    const saveWidgetConfig = useCallback(async (
        connectionId: DashboardWidgetEntry['connectionId'],
        widgetConfig: DashboardWidgetLayoutConfig,
    ): Promise<boolean> => {
        setSavingWidgetConfig(true);
        try {
            await saveCachedState(prev => {
                const nextWidgets = upsertSavedWidget(prev?.widgets ?? [], connectionId, {
                    type: widgetConfig.type,
                    variant: widgetConfig.variant,
                    underlyingDataVisibility: widgetConfig.underlyingDataVisibility,
                });
                setSavedWidgets(nextWidgets);
                return {
                    widgets: nextWidgets,
                    selectedCategory: prev?.selectedCategory,
                };
            });
            return true;
        } catch {
            return false;
        } finally {
            if (isMounted()) {
                setSavingWidgetConfig(false);
            }
        }
    }, [isMounted, saveCachedState]);

    /**
     * Updates and persists the selected stats category tab.
     */
    const setSelectedCategory = useCallback(async (category: string | undefined) => {
        setSelectedCategoryState(category);
        await saveCachedState(prev => ({
            widgets: prev?.widgets ?? [],
            selectedCategory: category,
        }));
    }, [saveCachedState]);

    return useMemo(() => ({
        loadingConfig,
        savingWidgetConfig,
        selectedCategory,
        setSelectedCategory,
        augmentCategorizedWidgets,
        saveWidgetOrderBy,
        saveWidgetConfig,
    }), [
        loadingConfig,
        savingWidgetConfig,
        selectedCategory,
        setSelectedCategory,
        augmentCategorizedWidgets,
        saveWidgetOrderBy,
        saveWidgetConfig,
    ]);
};

export default useStaticStatsWidgetsState;
